import { Component, Input, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export type ExportFormat = 'csv' | 'pdf' | 'excel';

export interface ExportColumn {
  key: string;
  label: string;
  selected: boolean;
}

export interface ExportConfig {
  title: string;
  description?: string;
  formats: ExportFormat[];
  columns: ExportColumn[];
  showDateRange?: boolean;
  showColumnSelection?: boolean;
}

export interface ExportOptions {
  format: ExportFormat;
  columns: string[];
  dateRange?: {
    start: string;
    end: string;
  };
  includeAll: boolean;
}

@Component({
  selector: 'app-export-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './export-modal.component.html',
  styleUrls: ['./export-modal.component.scss']
})
export class ExportModalComponent {
  @Input() config: ExportConfig = {
    title: 'Export Data',
    formats: ['csv', 'pdf', 'excel'],
    columns: [],
    showDateRange: true,
    showColumnSelection: true
  };

  @Output() exported = new EventEmitter<ExportOptions>();
  @Output() cancelled = new EventEmitter<void>();

  isOpen = signal(false);
  isExporting = signal(false);

  // Form state
  selectedFormat: ExportFormat = 'csv';
  includeAllColumns = true;
  dateRangeEnabled = false;
  startDate = '';
  endDate = '';

  // Track column selections
  columnSelections: Map<string, boolean> = new Map();

  /**
   * Open the export modal
   */
  open(config?: Partial<ExportConfig>): void {
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // Initialize column selections
    this.columnSelections.clear();
    this.config.columns.forEach(col => {
      this.columnSelections.set(col.key, col.selected);
    });

    // Reset form state
    this.selectedFormat = this.config.formats[0] || 'csv';
    this.includeAllColumns = true;
    this.dateRangeEnabled = false;
    this.startDate = '';
    this.endDate = '';

    this.isOpen.set(true);
  }

  /**
   * Close the modal
   */
  close(): void {
    if (this.isExporting()) return;
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  /**
   * Export with current options
   */
  export(): void {
    const selectedColumns = this.includeAllColumns
      ? this.config.columns.map(c => c.key)
      : this.config.columns
          .filter(c => this.columnSelections.get(c.key))
          .map(c => c.key);

    const options: ExportOptions = {
      format: this.selectedFormat,
      columns: selectedColumns,
      includeAll: this.includeAllColumns
    };

    if (this.dateRangeEnabled && this.startDate && this.endDate) {
      options.dateRange = {
        start: this.startDate,
        end: this.endDate
      };
    }

    this.exported.emit(options);
    this.isOpen.set(false);
  }

  /**
   * Set exporting state
   */
  setExporting(exporting: boolean): void {
    this.isExporting.set(exporting);
  }

  /**
   * Toggle column selection
   */
  toggleColumn(key: string): void {
    const current = this.columnSelections.get(key) || false;
    this.columnSelections.set(key, !current);
  }

  /**
   * Check if column is selected
   */
  isColumnSelected(key: string): boolean {
    return this.columnSelections.get(key) || false;
  }

  /**
   * Toggle all columns
   */
  toggleAllColumns(): void {
    this.includeAllColumns = !this.includeAllColumns;
    if (this.includeAllColumns) {
      this.config.columns.forEach(col => {
        this.columnSelections.set(col.key, true);
      });
    }
  }

  /**
   * Get format icon
   */
  getFormatIcon(format: ExportFormat): string {
    switch (format) {
      case 'csv':
        return 'bi-filetype-csv';
      case 'pdf':
        return 'bi-filetype-pdf';
      case 'excel':
        return 'bi-file-earmark-excel';
    }
  }

  /**
   * Get format label
   */
  getFormatLabel(format: ExportFormat): string {
    switch (format) {
      case 'csv':
        return 'CSV';
      case 'pdf':
        return 'PDF';
      case 'excel':
        return 'Excel';
    }
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Handle escape key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isExporting()) {
      this.close();
    }
  }

  /**
   * Check if export is valid
   */
  get canExport(): boolean {
    if (this.dateRangeEnabled) {
      if (!this.startDate || !this.endDate) return false;
      if (new Date(this.startDate) > new Date(this.endDate)) return false;
    }

    if (!this.includeAllColumns) {
      const hasSelectedColumns = this.config.columns.some(c => this.columnSelections.get(c.key));
      if (!hasSelectedColumns) return false;
    }

    return true;
  }
}
